import * as React from 'react';
import cs from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { range, some } from 'lodash';
import { SearchListing, SearchCriteria, StoreState } from '@root/types';
import savedActions from '@store/actions/saved';
import actions from '@store/actions/tourbooks';
import {
  hasCurrentUser,
  isListingSaved,
  getVisibleTourbookSummaries,
  tourbookCreateOnly,
  getLoadingState,
} from '@store/selectors';
import useAnalytics from '@shared/useAnalytics';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import usePrevious from '@root/shared/usePrevious';
import ListingCard from './ListingCard';
import s from './Cards.module.less';
import ListingCardSkeleton from './ListingCardSkeleton';
import EmptyState from './empty-state.svg';
import useListingSearchCriteria from '../utils/useListingSearchCriteria';

export type PassedProps = {
  currentPage?: number;
  searchCardClicked?: (
    e: React.MouseEvent,
    listing: SearchListing,
    searchFilters: SearchCriteria,
  ) => void;
  searchCardHovered?: (listing: SearchListing | null) => void;
  changePage?: (newPage: number) => void;
};

const mapState = (state: StoreState) => ({
  listingsCount: state.listings.pagination?.totalCount,
  ids: state.listings.pagination?.ids,
  pageSize: state.listings.pagination?.pageSize,
  listingsById: state.listings.searchResults,
  isSaved: (listingId: string) => isListingSaved(state, listingId),
  someoneIsLoggedIn: hasCurrentUser(state),
  tourbooks: getVisibleTourbookSummaries(state),
  istourbookCreateOnly: tourbookCreateOnly(state),
  isLoading: getLoadingState(state, 'listingSearch'),
});

const mapDispatch = {
  toggleSaveListing: ({ listing, willBeSaved, onSuccess }) =>
    savedActions.toggleSaveListing({
      listing,
      willBeSaved,
      onSuccess,
      sourcePage: 'search results page',
    }),
  fetchTourbooks: () => actions.fetchTourbookSummaries(),
};

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

export const RawCards = ({
  ids = [],
  listingsById,
  searchCardHovered = () => {},
  toggleSaveListing,
  isSaved,
  someoneIsLoggedIn,
  tourbooks,
  fetchTourbooks,
  isLoading,
}: PassedProps & ReduxProps) => {
  const hasSearchResults = ids && ids.length;
  const idsIterator = ids || [];

  const { clickToPage, PARAMETERS } = useAnalytics();
  const criteria = useListingSearchCriteria();
  const makeOnClick = (listing: SearchListing) => () => {
    clickToPage(
      {
        destination: PARAMETERS.listingPage,
        sourceContent: PARAMETERS.card,
        sourcePage: PARAMETERS.searchResultsPage,
        actionType: 'CLICKED_ON_LISTING_CARD',
      },
      {
        isExclusiveListing: listing.isCurrentlyExclusive,
        isFlexibleSizeSearch: !!listing.minMaxArea,
        currentFilters: criteria.toAnalyticsProperties(),
      },
    );
  };

  React.useEffect(() => {
    if (someoneIsLoggedIn) fetchTourbooks();
  }, [someoneIsLoggedIn]);

  const { t } = useTranslation('redesignedListingSearch');
  const isInTourbook = (listingId: string): boolean =>
    some(tourbooks, tourbook => tourbook.listingIds.includes(listingId));

  // We only want to display skeleton cards on first load.
  // The below logic stops displaying the skeleton cards the
  // first time isLoading changes from true to false
  const previousIsLoading = usePrevious(isLoading);
  const [shouldDisplaySkeleton, setShouldDisplaySkeleton] = useState(true);
  useEffect(() => {
    setShouldDisplaySkeleton(shouldDisplaySkeleton && !previousIsLoading);
  }, [isLoading]);

  const numberOfSkeletonCards = isLoading ? 24 : idsIterator.length;
  const skeletonCards = (
    <ul
      data-testid="skeletonCardList"
      className={cs(s.skeletonCardList, !shouldDisplaySkeleton && s.invisible)}
    >
      {range(numberOfSkeletonCards).map(i => (
        <li className={s.listingCardWrapper} key={i}>
          <ListingCardSkeleton />
        </li>
      ))}
    </ul>
  );
  return (
    <section className={s.container}>
      {hasSearchResults ? (
        <ul className={s.cardList} id="listing-cards">
          {idsIterator.map(listingId => {
            const listing = listingsById[listingId];
            if (!listing) return null;

            return (
              <li className={s.listingCardWrapper} key={listingId}>
                <ListingCard
                  listing={listing}
                  onHover={() => searchCardHovered(listing)}
                  onHoverExit={() => searchCardHovered(null)}
                  toggleSaveListing={toggleSaveListing}
                  favorited={isSaved(listingId)}
                  loggedInUser={someoneIsLoggedIn}
                  tourbooks={tourbooks}
                  isInTourbook={isInTourbook(listingId)}
                  onClick={makeOnClick(listing)}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        !shouldDisplaySkeleton && (
          <div data-testid="noResultsView" className={s.emptySearch}>
            <EmptyState />
            <h4>{t('noResultsFound')}</h4>
            <p>{t('differentSearchCriteria')}</p>
          </div>
        )
      )}
      {skeletonCards}
    </section>
  );
};

export default connector(React.memo(RawCards));
